import { useNavigate } from "react-router-dom";
import { Headphones, Clock, ArrowLeft, ArrowRight, FastForward } from "lucide-react";
import { LIBRARY_PRACTICES, renderInlineItalic } from "../lib/libraryPractices";
import { getProgress, formatProgressTime } from "../lib/practiceProgress";
import { AppMenu } from "../components/AppMenu";
import { SiteFooter } from "../components/SiteFooter";

// Audio Library page. Cream aesthetic matches the rest of the app.

const PracticeCard = ({ practice, onOpen }) => {
  const progress = getProgress(practice.id);
  const progressPercent =
    progress && progress.duration > 0
      ? Math.min(100, (progress.position / progress.duration) * 100)
      : 0;
  return (
  <button
    onClick={() => onOpen(practice.id)}
    className="text-left p-0 bg-white rounded-3xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_10px_36px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 flex flex-col w-full"
    data-testid={`library-card-${practice.id}`}
  >
    <div
      className="h-56 sm:h-64 w-full bg-cover relative shrink-0"
      style={{
        backgroundImage: `url('${practice.image}')`,
        backgroundPosition: practice.imagePosition || "center",
      }}
    >
      {progress && (
        <span
          className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/95 text-[#1B2349] shadow-sm"
          data-testid={`library-card-resume-${practice.id}`}
        >
          <FastForward className="w-3 h-3" />
          Continue · {formatProgressTime(progress.position)}
        </span>
      )}
    </div>
    <div className="p-7 sm:p-8 flex-1">
      <div className="flex items-center gap-2 text-sm text-[#718096] mb-3 flex-wrap">
        <Headphones className="w-4 h-4" />
        <span>Audio</span>
        <span className="opacity-60">·</span>
        <Clock className="w-4 h-4" />
        <span>{practice.duration} · {practice.category}</span>
        {practice.freePreview && (
          <span
            className="ml-auto px-2.5 py-1 rounded-full text-xs font-medium text-white uppercase tracking-wider"
            style={{ backgroundColor: "#306090" }}
          >
            Free preview
          </span>
        )}
      </div>
      <h3 className="font-outfit font-semibold text-2xl text-[#1B2349] tracking-tight mb-3 leading-tight">
        {practice.title}
      </h3>
      <p className="text-[#4A5568] leading-relaxed mb-5">
        {renderInlineItalic(practice.description).map((s) =>
          s.italic ? (
            <em key={s.key}>{s.value}</em>
          ) : (
            <span key={s.key}>{s.value}</span>
          )
        )}
      </p>
      {progress && (
        <div
          className="mb-4"
          aria-label={`Listened to ${Math.round(progressPercent)} percent`}
        >
          <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-[#3090A8]"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}
      <div className="inline-flex items-center gap-1 text-[#306090] font-medium">
        {progress ? "Continue practice" : "Open practice"} <ArrowRight className="w-4 h-4" />
      </div>
    </div>
  </button>
  );
};

const LibraryPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="library-page">
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="px-6 sm:px-10 py-5 flex items-center justify-between max-w-7xl mx-auto">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-[#4A5568] hover:text-[#1B2349] flex items-center gap-2 px-3 py-2 rounded-full hover:bg-white/60"
            data-testid="library-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </button>
          <AppMenu />
        </div>
      </header>

      <section className="px-6 sm:px-10 pt-6 pb-10 max-w-7xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-5">
          Daily Practices
        </p>
        <h1 className="font-outfit font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#1B2349] tracking-tight mb-10 max-w-3xl leading-[1.1]">
          Practices for every part of your day.
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {LIBRARY_PRACTICES.map((p) => (
            <PracticeCard
              key={p.id}
              practice={p}
              onOpen={(id) => navigate(`/library/${id}`)}
            />
          ))}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default LibraryPage;
